import classnames from 'classnames/bind';
import { useClipboard } from '@mantine/hooks';
import { CellProps } from 'react-table';

import { Stream } from 'types/stream';

import { useModal } from 'hooks/useModal';
import { useDeleteStreams } from 'streams/hooks/useDeleteStreams';

import { Button } from 'components/Button/Button';
import {
  ContextMenu,
  ContextMenuAction,
  ContextMenuSeparator,
} from 'components/ContextMenu/ContextMenu';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { Tooltip } from 'components/Tooltip/Tooltip';

import styles from './Table.module.scss';

const c = classnames.bind(styles);

export type StreamsTableActionsCellProps = CellProps<Stream> & {
  onEmbedClick?: (streamID: string) => void;
  onRenameClick?: (streamID: string) => void;
};

export function StreamsTableActionsCell({
  row,
  onEmbedClick,
  onRenameClick,
}: StreamsTableActionsCellProps) {
  const stream = row.original;

  const { open } = useModal();

  const { copied: isCopied, copy } = useClipboard({ timeout: 2000 });

  const { mutateAsync: deleteStreams } = useDeleteStreams();

  function handleRenameClick() {
    if (!stream.id) {
      return;
    }

    onRenameClick?.(stream.id);
  }

  function handleDeleteClick() {
    open('confirm-delete', {
      title: `Are you sure you want to delete ${stream.name}?`,
      onConfirm: () => deleteStreams({ stream_ids: [stream.id] }),
    });
  }

  return (
    <div className={c('stream-actions', 'columns halign-end valign-center')}>
      {stream.uri && (
        <Tooltip content="Copied to clipboard!" side="right" open={isCopied}>
          <Button
            variant="ghost"
            size="xsmall"
            onClick={() => {
              copy(stream.uri!);
            }}
          >
            Copy URL
          </Button>
        </Tooltip>
      )}

      {stream.stream_type === 'webrtc' && onEmbedClick && (
        <Button
          variant="secondary"
          onClick={() => onEmbedClick(stream.id as string)}
          size="xsmall"
        >
          Embed
        </Button>
      )}

      <ContextMenu
        trigger={
          <IconButton
            icon="more-vertical"
            label="Reveal more actions"
            variant="ghost"
          />
        }
      >
        {onRenameClick && (
          <ContextMenuAction onClick={handleRenameClick} icon="text">
            Rename
          </ContextMenuAction>
        )}
        <ContextMenuSeparator />
        <ContextMenuAction
          onClick={handleDeleteClick}
          icon="delete"
          intent="danger"
        >
          Delete
        </ContextMenuAction>
      </ContextMenu>
    </div>
  );
}
